import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import service from '../../store/store/baseapiservice';

interface ComplaintStatusDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentLanguage: string;
}

interface GrievanceDetails {
  tckt_id: string;
  tckt_sts_nm: string;
  tckt_ctgry_nm: string;
  tckt_sb_ctgry_nm: string;
  lng_dscn_tx: string;
  wrd_nm?: string;
  svm_nm?: string;
  tckt_dt: string;
  assignuser_fstnm?: string;
  lat?: number;
  lng?: number;
}

interface Attachment {
  atcht_url_tx: string;
  fl_type: string;
}

interface HistoryItem {
  stg: number;
  ky_nm: string;
  prvsname?: string;
  crntname?: string;
  cmnt_tx?: string;
  fst_nm: string;
  chng_dt?: string;
  atchmnts?: Attachment[];
}

export default function ComplaintStatusDrawer({ isOpen, onClose, currentLanguage }: ComplaintStatusDrawerProps) {
  const [ticketRef, setTicketRef] = useState('');
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [captchaId, setCaptchaId] = useState<number | null>(null);
  const [saltKey, setSaltKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCaptcha, setIsLoadingCaptcha] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [grievanceDetails, setGrievanceDetails] = useState<GrievanceDetails | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [showGrievanceDetails, setShowGrievanceDetails] = useState(false);

  // Fetch captcha when drawer opens
  useEffect(() => {
    if (isOpen) {
      fetchCaptcha();
      // Reset form when drawer opens
      setTicketRef('');
      setCaptchaCode('');
      setError(null);
      setGrievanceDetails(null);
      setAttachments([]);
      setHistory([]);
      setShowGrievanceDetails(false);
    }
  }, [isOpen]);

  const fetchCaptcha = async () => {
    try {
      setIsLoadingCaptcha(true);
      setError(null);
      const response = await service.get('auth2/ss/login/capcha');
      
      if (response.status === 200 && response.data) {
        setCaptchaImage(response.data.cptch_img_src_tx || response.data.data);
        setCaptchaId(response.data.cptch_id || response.data.text);
        setSaltKey(response.data.salt_ky);
      } else {
        setError('Failed to load captcha. Please try again.');
      }
    } catch (err: any) {
      console.error('Captcha fetch error:', err);
      setError('Failed to load captcha. Please try again.');
    } finally {
      setIsLoadingCaptcha(false);
    }
  };

  const handleRefreshCaptcha = () => {
    fetchCaptcha();
    setCaptchaCode('');
  };

  const fetchAttachments = async (ticketId: string) => {
    try {
      const response = await service.get(`open/grievances/tickets/${ticketId}/attachments`);
      if (response.status === 200 && response.data) {
        setAttachments(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      console.error('Error fetching attachments:', err);
      setAttachments([]);
    }
  };

  const fetchHistory = async (ticketId: string) => {
    try {
      const response = await service.get(`support/grievances/ticket/${ticketId}/history`);
      if (response.status === 200 && response.data) {
        setHistory(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
      setHistory([]);
    }
  };

  const handleSearch = async () => {
    if (!ticketRef.trim()) {
      setError(currentLanguage === 'en' ? 'Please enter a ticket reference number' : 'దయచేసి టికెట్ రిఫరెన్స్ నంబర్ నమోదు చేయండి');
      return;
    }

    if (!captchaCode.trim()) {
      setError(currentLanguage === 'en' ? 'Please enter the captcha code' : 'దయచేసి క్యాప్చా కోడ్ నమోదు చేయండి');
      return;
    }

    if (!captchaId || !saltKey) {
      setError(currentLanguage === 'en' ? 'Captcha not loaded. Please refresh and try again.' : 'క్యాప్చా లోడ్ కాలేదు. దయచేసి రిఫ్రెష్ చేసి మళ్లీ ప్రయత్నించండి.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setGrievanceDetails(null);
      setAttachments([]);
      setHistory([]);
      setShowGrievanceDetails(false);

      // Check if user has authentication token
      // The public/web route requires authentication, open route doesn't
      const token = localStorage.getItem('token') || localStorage.getItem('x-access-token') || sessionStorage.getItem('token') || sessionStorage.getItem('x-access-token');
      const route = token
        ? `public/web/grievance/tickets/${ticketRef.trim()}/type/2`
        : `open/grievance/tickets/${ticketRef.trim()}/type/2`;

      // Ensure captchaID is a number
      const captchaIdNum = typeof captchaId === 'number' ? captchaId : parseInt(String(captchaId), 10);
      
      if (isNaN(captchaIdNum)) {
        setError(currentLanguage === 'en' ? 'Invalid captcha. Please refresh and try again.' : 'చెల్లని క్యాప్చా. దయచేసి రిఫ్రెష్ చేసి మళ్లీ ప్రయత్నించండి.');
        fetchCaptcha();
        setCaptchaCode('');
        return;
      }

      // Prepare request body
      const requestBody = {
        captcha: captchaCode.trim(),
        captchaID: captchaIdNum
      };

      // Add token header if available
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['x-access-token'] = token;
      }

      // Use direct fetch to have control over headers
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4901/apiv1';
      const url = `${API_BASE_URL}/${route}`;
      
      const fetchResponse = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody),
        credentials: 'include',
      });

      const data = await fetchResponse.json();
      
      // Handle response
      const response = {
        status: fetchResponse.status,
        data: data.data || data,
        message: data.message,
        error: data.error
      };

      if (response.status === 200 && response.data) {
        const dataArray = Array.isArray(response.data) ? response.data : [response.data];
        if (dataArray.length > 0) {
          setGrievanceDetails(dataArray[0]);
          setShowGrievanceDetails(true);
          setError(null);
          
          // Fetch attachments and history
          await Promise.all([
            fetchAttachments(dataArray[0].tckt_id),
            fetchHistory(dataArray[0].tckt_id)
          ]);
        } else {
          setError(currentLanguage === 'en' ? 'No Data' : 'డేటా లేదు');
          setShowGrievanceDetails(false);
        }
      } else if (response.status === 411 || response.status === 601 || response.status === 406) {
        setError(response.message || (currentLanguage === 'en' ? 'Invalid captcha. Please try again.' : 'చెల్లని క్యాప్చా. దయచేసి మళ్లీ ప్రయత్నించండి.'));
        setShowGrievanceDetails(false);
        // Refresh captcha on error
        fetchCaptcha();
        setCaptchaCode('');
      } else {
        setError(response.message || (currentLanguage === 'en' ? 'Failed to fetch complaint status. Please try again.' : 'ఫిర్యాదు స్థితిని పొందడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.'));
        setShowGrievanceDetails(false);
        fetchCaptcha();
        setCaptchaCode('');
      }
    } catch (err: any) {
      console.error('Search error:', err);
      setError(err.message || (currentLanguage === 'en' ? 'Something went wrong. Please try again.' : 'ఏదో తప్పు జరిగింది. దయచేసి మళ్లీ ప్రయత్నించండి.'));
      setShowGrievanceDetails(false);
      fetchCaptcha();
      setCaptchaCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-[10000]"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-[10000] flex flex-col"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-4 flex items-center justify-between">
              <h2 className="text-white font-semibold text-lg">
                {currentLanguage === 'en' ? 'eComplaints Status' : 'ఇ-ఫిర్యాదు స్థితి'}
              </h2>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Ticket Reference Number */}
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  {currentLanguage === 'en' ? 'Ticket Reference Number' : 'టికెట్ రిఫరెన్స్ నంబర్'}
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg font-bold">
                    #
                  </div>
                  <input
                    type="text"
                    value={ticketRef}
                    onChange={(e) => setTicketRef(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={currentLanguage === 'en' ? 'Enter ticket number' : 'టికెట్ నంబర్ నమోదు చేయండి'}
                    className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  />
                  {ticketRef.trim() && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500">
                      <i className="ri-check-line text-xl"></i>
                    </div>
                  )}
                </div>
              </div>

              {/* Captcha Section */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex-1 bg-gray-100 border border-gray-300 rounded-lg p-4 flex items-center justify-center min-h-[60px]">
                    {isLoadingCaptcha ? (
                      <div className="text-gray-400">
                        <i className="ri-loader-4-line text-2xl animate-spin"></i>
                      </div>
                    ) : captchaImage ? (
                      <img 
                        src={captchaImage} 
                        alt="Captcha" 
                        className="max-h-16"
                      />
                    ) : (
                      <div className="text-gray-400 text-sm">
                        {currentLanguage === 'en' ? 'Captcha not loaded' : 'క్యాప్చా లోడ్ కాలేదు'}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleRefreshCaptcha}
                    disabled={isLoadingCaptcha}
                    className="w-12 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-full flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={currentLanguage === 'en' ? 'Refresh captcha' : 'క్యాప్చా రిఫ్రెష్ చేయండి'}
                  >
                    <i className="ri-refresh-line text-xl"></i>
                  </button>
                </div>
                <label className="block text-gray-700 font-medium mb-2 text-sm">
                  {currentLanguage === 'en' ? 'Enter the code from the above image' : 'పై చిత్రం నుండి కోడ్ నమోదు చేయండి'}
                </label>
                 <input
                   type="text"
                   value={captchaCode}
                   onChange={(e) => setCaptchaCode(e.target.value)}
                   onKeyPress={handleKeyPress}
                   placeholder={currentLanguage === 'en' ? 'Enter captcha code' : 'క్యాప్చా కోడ్ నమోదు చేయండి'}
                   className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                 />
              </div>

              {/* Search Button */}
              <button
                onClick={handleSearch}
                disabled={isLoading || isLoadingCaptcha}
                className="w-full bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-semibold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    {currentLanguage === 'en' ? 'Searching...' : 'శోధిస్తోంది...'}
                  </span>
                ) : (
                  currentLanguage === 'en' ? 'SEARCH' : 'శోధించు'
                )}
              </button>

              {/* Error/Status Message */}
              {error && !showGrievanceDetails && (
                <div className="mt-4 text-center">
                  <p className={`text-sm ${error === 'No Data' || error === 'డేటా లేదు' ? 'text-red-600' : 'text-red-500'}`}>
                    {error}
                  </p>
                </div>
              )}

              {/* Grievance Details Display */}
              {showGrievanceDetails && grievanceDetails && (
                <div className="mt-6 space-y-4">
                  {/* Ticket Number Header */}
                  <div className="bg-orange-50 px-4 py-3 rounded-lg border-l-4 border-orange-500">
                    <h3 className="font-bold text-orange-900">
                      {currentLanguage === 'en' ? 'TICKET NO' : 'టికెట్ నంబర్'}: #{grievanceDetails.tckt_id}
                    </h3>
                  </div>

                  {/* Details Grid */}
                  <div className="space-y-3">
                    <DetailRow 
                      label={currentLanguage === 'en' ? 'Status' : 'స్థితి'} 
                      value={grievanceDetails.tckt_sts_nm} 
                    />
                    <DetailRow 
                      label={currentLanguage === 'en' ? 'Category' : 'వర్గం'} 
                      value={grievanceDetails.tckt_ctgry_nm} 
                    />
                    <DetailRow 
                      label={currentLanguage === 'en' ? 'Sub-Category' : 'ఉప-వర్గం'} 
                      value={grievanceDetails.tckt_sb_ctgry_nm} 
                    />
                    <DetailRow 
                      label={currentLanguage === 'en' ? 'Complaint' : 'ఫిర్యాదు'} 
                      value={grievanceDetails.lng_dscn_tx} 
                    />
                    <DetailRow 
                      label={currentLanguage === 'en' ? 'Ward' : 'వార్డు'} 
                      value={grievanceDetails.wrd_nm || 'NA'} 
                    />
                    <DetailRow 
                      label={currentLanguage === 'en' ? 'Sachivalayam' : 'సచివాలయం'} 
                      value={grievanceDetails.svm_nm || 'NA'} 
                    />
                    <DetailRow 
                      label={currentLanguage === 'en' ? 'Created Date' : 'సృష్టించిన తేదీ'} 
                      value={grievanceDetails.tckt_dt} 
                    />
                    <DetailRow 
                      label={currentLanguage === 'en' ? 'Assigned To' : 'కేటాయించబడింది'} 
                      value={grievanceDetails.assignuser_fstnm || 'NA'} 
                    />
                  </div>

                  {/* Location Section */}
                  <div className="mt-4">
                    <h4 className="font-semibold text-gray-800 mb-2 px-2">
                      {currentLanguage === 'en' ? 'Location' : 'స్థానం'}
                    </h4>
                    {grievanceDetails.lat && grievanceDetails.lng ? (
                      <a
                        href={`https://www.google.co.in/maps/place/${grievanceDetails.lat},${grievanceDetails.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium"
                      >
                        {currentLanguage === 'en' ? 'View Location On Map' : 'మ్యాప్‌లో స్థానాన్ని వీక్షించండి'}
                      </a>
                    ) : (
                      <p className="text-gray-500 text-sm px-2">
                        {currentLanguage === 'en' ? 'No Location' : 'స్థానం లేదు'}
                      </p>
                    )}
                  </div>

                  {/* Photos/Documents Section */}
                  <div className="mt-4">
                    <h4 className="font-semibold text-gray-800 mb-2 px-2">
                      {currentLanguage === 'en' ? 'Photos/Documents' : 'ఫోటోలు/పత్రాలు'}
                    </h4>
                    {attachments.length > 0 ? (
                      <div className="grid grid-cols-3 gap-3 px-2">
                        {attachments.map((attachment, index) => (
                          <AttachmentItem key={index} attachment={attachment} />
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm text-center px-2">
                        {currentLanguage === 'en' ? 'No Images/Documents' : 'ఫోటోలు/పత్రాలు లేవు'}
                      </p>
                    )}
                  </div>

                  {/* Ticket History Section */}
                  <div className="mt-4">
                    <h4 className="font-semibold text-gray-800 mb-3 px-2">
                      {currentLanguage === 'en' ? 'Ticket History' : 'టికెట్ చరిత్ర'}
                    </h4>
                    {history.length > 0 ? (
                      <div className="space-y-4 px-2">
                        {history.map((item, index) => (
                          <HistoryItemComponent key={index} item={item} currentLanguage={currentLanguage} />
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm text-center px-2">
                        {currentLanguage === 'en' ? 'No History' : 'చరిత్ర లేదు'}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Helper component for detail rows
function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start border-b border-gray-200 pb-2">
      <div className="w-2/5 text-gray-700 font-medium text-sm">{label}</div>
      <div className="w-1/12 text-gray-500 text-center">:</div>
      <div className="w-2/5 text-gray-900 text-sm">{value}</div>
    </div>
  );
}

// Helper component for attachment items
function AttachmentItem({ attachment }: { attachment: Attachment }) {
  const isImage = attachment.fl_type === 'image/jpeg' || 
                  attachment.fl_type === 'image/jpg' || 
                  attachment.fl_type === 'image/png';
  const isWord = attachment.fl_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                 attachment.fl_type === 'application/msword';
  const isExcel = attachment.fl_type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                  attachment.fl_type === 'application/vnd.ms-excel';
  const isPdf = attachment.fl_type === 'application/pdf';

  if (isImage) {
    return (
      <div className="cursor-pointer hover:opacity-80 transition-opacity">
        <img 
          src={attachment.atcht_url_tx} 
          alt="Attachment" 
          className="w-full h-20 object-cover rounded border border-gray-300"
          onClick={() => window.open(attachment.atcht_url_tx, '_blank')}
        />
      </div>
    );
  }

  if (isWord || isExcel) {
    return (
      <a href={attachment.atcht_url_tx} target="_blank" rel="noopener noreferrer" className="block">
        <div className="w-full h-20 flex items-center justify-center rounded border border-gray-300 bg-gray-50 hover:bg-gray-100 transition-colors">
          <i className="ri-file-word-2-line text-4xl text-blue-600"></i>
        </div>
      </a>
    );
  }

  if (isPdf) {
    return (
      <a href={attachment.atcht_url_tx} target="_blank" rel="noopener noreferrer" className="block">
        <div className="w-full h-20 flex items-center justify-center rounded border border-gray-300 bg-gray-50 hover:bg-gray-100 transition-colors">
          <i className="ri-file-pdf-line text-4xl text-red-600"></i>
        </div>
      </a>
    );
  }

  return null;
}

// Helper component for history items
function HistoryItemComponent({ item, currentLanguage }: { item: HistoryItem; currentLanguage: string }) {
  return (
    <div className="border-l-2 border-orange-500 pl-4 pb-4 relative">
      <div className="absolute -left-2 top-0 w-4 h-4 bg-orange-500 rounded-full"></div>
      <div className="flex items-center gap-2 mb-2">
        <span className="bg-orange-100 text-orange-800 text-xs font-semibold px-2 py-1 rounded">
          {item.stg}
        </span>
        <span className="font-medium text-gray-900">{item.ky_nm}</span>
      </div>
      
      {item.prvsname && (
        <p className="text-xs text-gray-600 mb-1">
          <span className="font-semibold">{currentLanguage === 'en' ? 'From' : 'నుండి'}:</span> {item.prvsname}
        </p>
      )}
      
      {item.crntname && (
        <p className="text-xs text-gray-600 mb-1">
          <span className="font-semibold">{currentLanguage === 'en' ? 'To' : 'కు'}:</span> {item.crntname}
        </p>
      )}
      
      {item.cmnt_tx && (
        <p className="text-xs text-gray-600 mb-1">
          <span className="font-semibold">{currentLanguage === 'en' ? 'Comment' : 'వ్యాఖ్య'}:</span> {item.cmnt_tx}
        </p>
      )}

      {item.atchmnts && item.atchmnts.length > 0 && (
        <div className="flex gap-2 mt-2 flex-wrap">
          {item.atchmnts.map((att, idx) => (
            <AttachmentItem key={idx} attachment={att} />
          ))}
        </div>
      )}

      <p className="text-xs text-gray-600 mt-1">
        <span className="font-semibold">{currentLanguage === 'en' ? 'By' : 'ద్వారా'}:</span> {item.fst_nm}
      </p>
      
      {item.chng_dt && (
        <p className="text-xs text-gray-500 text-right mt-1">
          {item.chng_dt}
        </p>
      )}
    </div>
  );
}

